/** Internal dependencies */
import { fetchHandler } from '../../../../../../assets/src/scripts/utilities/fetchHandler';

export const checkout = {
    updateTimer: false,
    dirtyInput: false,
    selectedPaymentMethod: false,
    fetch: false,
    orderReview: document.querySelector('#order_review'),
    checkoutForm: document.querySelector('form.checkout'),
    parentEl: document.querySelector('.ct_woocommercecheckout'),
    checkoutParams: null,
    events: {
        change: new Event('change', {
            bubbles: true,
        }),
        initCheckout: new CustomEvent('initCheckout'),
        click: new Event('click', {
            bubbles: true,
        }),
        paymentMethodSelected: new CustomEvent('paymentMethodSelected'),
        updateCheckout: new CustomEvent('updateCheckout'),
        validate: new Event('validate', { bubbles: true }),
        blur: new Event('blur'),
        initShippingAddressToggle: new CustomEvent('initShippingAddressToggle', { bubbles: true }),
    },
    abortController: new AbortController(),

    init: function () {
        // bind this
        this.initPaymentMethods = this.initPaymentMethods.bind(this);
        this.getPaymentMethod = this.getPaymentMethod.bind(this);
        this.paymentMethodSelected = this.paymentMethodSelected.bind(this);
        this.toggleCreateAccount = this.toggleCreateAccount.bind(this);
        this.initCheckout = this.initCheckout.bind(this);
        this.maybeInputChanged = this.maybeInputChanged.bind(this);
        this.inputChanged = this.inputChanged.bind(this);
        this.queueUpdateCheckout = this.queueUpdateCheckout.bind(this);
        this.triggerUpdateCheckout = this.triggerUpdateCheckout.bind(this);
        this.maybeUpdateCheckout = this.maybeUpdateCheckout.bind(this);
        this.resetUpdateCheckoutTimer = this.resetUpdateCheckoutTimer.bind(this);
        this.updateCheckout = this.updateCheckout.bind(this);
        this.updateCheckoutAction = this.updateCheckoutAction.bind(this);
        this.submitOrder = this.submitOrder.bind(this);
        this.submit = this.submit.bind(this);
        this.submitError = this.submitError.bind(this);

        this.checkoutParams = this.parentEl ? JSON.parse(this.parentEl.dataset.wcCheckoutParams) : null;

        document.body.addEventListener('updateCheckout', this.updateCheckout);
        document.body.addEventListener('initCheckout', this.initCheckout);

        if (this.orderReview && this.orderReview.classList.contains('sys_form-pay')) {
            this.orderReview.addEventListener('click', this.paymentMethodSelected);
            this.orderReview.addEventListener('submit', this.submitOrder);
            this.orderReview.setAttribute('novalidate', 'novalidate');
        }

        if (this.checkoutForm) {
            this.checkoutForm.addEventListener('click', this.paymentMethodSelected);

            // prevent html5 validation
            this.checkoutForm.setAttribute('novalidate', 'novalidate');

            // form submission
            this.checkoutForm.addEventListener('submit', this.submit);

            // inline validation
            this.checkoutForm.addEventListener('input', this.validateField);
            this.checkoutForm.addEventListener('validate', this.validateField);
            this.checkoutForm.addEventListener('change', this.validateField);

            // manual trigger
            this.checkoutForm.addEventListener('update', this.triggerUpdateCheckout);

            // form controls that update totals
            this.checkoutForm.addEventListener('change', this.triggerUpdateCheckout);
            this.checkoutForm.addEventListener('change', this.inputChanged);
            this.checkoutForm.addEventListener('change', this.maybeInputChanged);
            this.checkoutForm.addEventListener('keydown', this.queueUpdateCheckout);

            // address fields
            this.checkoutForm.addEventListener('change', this.shipToDifferentAddress);
            this.checkoutForm.addEventListener('initShippingAddressToggle', this.shipToDifferentAddress);

            // Trigger events
            const differentAddressInput = this.checkoutForm.querySelector('#ship-to-different-address input');
            if (differentAddressInput) {
                // let's use a custom event here so we can differentiate actions in the callback function, specifically here we only want to show the shipping address accordion if the user clicked the checkbox manually
                differentAddressInput.dispatchEvent(this.events.initShippingAddressToggle);
            }
        }

        this.initPaymentMethods();

        // Update on page load
        if (this.checkoutParams?.isCheckout === '1') {
            document.body.dispatchEvent(this.events.initCheckout);
        }

        if (this.checkoutParams?.optionGuestCheckout === 'yes') {
            const createAccount = document.querySelector('input#createaccount');
            if (createAccount) {
                createAccount.addEventListener('change', this.toggleCreateAccount);
                createAccount.dispatchEvent(this.events.change);
            }
        }
    },

    initPaymentMethods: function () {
        if (!this.parentEl) {
            return;
        }

        const paymentMethods = [].slice.call(this.parentEl.querySelectorAll('input[name="payment_method"]'));

        // if there is only one method, we can hide the radio
        if (paymentMethods && paymentMethods.length === 1) {
            paymentMethods[0].classList.add('d-none');
        }

        // if there was a previously selected method, check that one
        if (this.selectedPaymentMethod) {
            const methodInput = document.getElementById(this.selectedPaymentMethod);
            if (methodInput) {
                methodInput.checked = true;
            }
        }

        // if none are selected, check the first
        if (paymentMethods && paymentMethods.length > 0 && !paymentMethods.some((method) => method.checked)) {
            paymentMethods[0].checked = true;
        }

        // Get name of newly selected method
        const checkedPaymentMethod = paymentMethods.filter((method) => method.checked)[0];

        if (checkedPaymentMethod) {
            if (paymentMethods.length > 1) {
                // hide open descriptions
                const paymentBoxes = this.parentEl.querySelectorAll(`div.payment-box`);
                if (paymentBoxes) {
                    paymentBoxes.forEach((box) => {
                        if (box.classList.contains(checkedPaymentMethod.id)) {
                            box.classList.remove('d-none');
                        } else {
                            box.classList.add('d-none');
                        }
                    });
                }
            }

            // trigger click event for selected method
            checkedPaymentMethod.dispatchEvent(this.events.click);
        }
    },

    getPaymentMethod: function () {
        const checkedMethod = this.checkoutForm.querySelector('input[name="payment_method"]:checked');
        if (checkedMethod) {
            return checkedMethod.value;
        }
    },

    paymentMethodSelected: function (e) {
        const target = e.target;
        if (!target.name || !target.name === 'payment_method') {
            return;
        }

        e.stopPropagation();

        const paymentMethodInputs = this.parentEl.querySelectorAll('.payment-method input[type="radio"]');

        if (paymentMethodInputs) {
            if (paymentMethodInputs.length > 1) {
                const targetPaymentBox = this.parentEl.querySelector(`div.payment-box.${target.id}`);
                const isChecked = target.checked;

                if (isChecked) {
                    const allPaymentBoxes = this.parentEl.querySelectorAll('div.payment-box');
                    allPaymentBoxes && allPaymentBoxes.forEach((box) => box.classList.add('d-none'));
                    targetPaymentBox?.classList.remove('d-none');
                }
            } else {
                const paymentBoxes = this.parentEl.querySelectorAll('div.payment-box');
                paymentBoxes && paymentBoxes.forEach((box) => box.classList.remove('d-none'));
            }

            const placeOrder = this.parentEl.querySelector('#place_order');
            if (target.dataset.orderButtonText) {
                placeOrder.textContent = target.dataset.orderButtonText;
            } else {
                placeOrder.textContent = placeOrder.dataset.value;
            }

            const selectedPaymentMethod = this.parentEl.querySelector('input[name="payment_method"]:checked');

            if (selectedPaymentMethod && selectedPaymentMethod.id !== this.selectedPaymentMethod) {
                document.body.dispatchEvent(this.events.paymentMethodSelected);
                this.selectedPaymentMethod = selectedPaymentMethod.id;
            }
        }
    },

    validateField: function (e) {
        const target = e.target;

        // check that we have an input field we actually want to validate
        if (!target.matches('.form-control, .form-select, .form-check-input')) {
            return;
        }

        const parent = target.closest('.form-floating');
        if (!parent) {
            // no parent > no validation needed, atm only happens for the ship-to-different-address checkbox
            return;
        }
        let validated = true;
        const validateRequired = parent.classList.contains('validate-required');
        const validateEmail = parent.classList.contains('validate-email');
        const validatePhone = parent.classList.contains('validate-phone');
        let pattern = '';
        const eventType = e.type;

        // if no validation is required just return
        if (!validateRequired && !validateEmail && !validatePhone) {
            return;
        }

        if (eventType === 'input') {
            target.classList.remove('is-invalid');
        }

        if (eventType === 'validate' || eventType === 'change') {
            if (validateRequired) {
                if (target.type === 'checkbox' && !target.checked) {
                    target.classList.remove('is-valid');
                    target.classList.add('is-invalid');
                    validated = false;
                } else if (target.value === '') {
                    target.classList.remove('is-valid');
                    target.classList.add('is-invalid');
                    validated = false;
                }
            }

            if (validateEmail) {
                if (target.value) {
                    // see https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
                    pattern =
                        /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

                    if (!pattern.test(String(target.value).toLowerCase())) {
                        target.classList.remove('is-valid');
                        target.classList.add('is-invalid');
                        validated = false;
                    }
                }
            }

            if (validatePhone) {
                pattern = /[\s\#0-9_\-\+\/\(\)\.]/g;

                if (target.value.replace(pattern, '').length > 0) {
                    target.classList.remove('is-valid');
                    target.classList.add('is-invalid');
                    validated = false;
                }
            }

            if (validated) {
                target.classList.remove('is-invalid');
                target.classList.add('is-valid');
                // parent.closest('form').classList.add('was-validated');
            }
        }
    },

    triggerUpdateCheckout: function (e = undefined) {
        if (e !== undefined) {
            const target = e.target;
            if (
                !target.matches(
                    'select.shipping_method, input[name^="shipping_method"], #ship-to-different-address input, .update_totals_on_change select, .update_totals_on_change input[type="radio"], .update_totals_on_change input[type="checkbox"]'
                )
            ) {
                return;
            }
        }

        this.resetUpdateCheckoutTimer();
        this.dirtyInput = false;
        document.body.dispatchEvent(this.events.updateCheckout);
    },

    inputChanged: function (e) {
        const target = e.target;

        if (!target.matches('.address-field select')) {
            return;
        }
        this.dirtyInput = e.target;
        this.maybeUpdateCheckout();
    },

    maybeInputChanged: function (e) {
        const target = e.target;

        if (!target.matches('.address-field input.form-control, .update_totals_on_change input.form-control')) {
            return;
        }

        if (this.dirtyInput) {
            this.inputChanged(e);
        }
    },

    queueUpdateCheckout: function (e) {
        const target = e.target;

        if (!target.matches('.address-field input.form-control, .update_totals_on_change input.form-control')) {
            return;
        }

        const code = e.keyCode || e.which || 0;

        if (code === 9) {
            return true;
        }

        this.dirtyInput = target;
        this.resetUpdateCheckoutTimer();
        this.updateTimer = setTimeout(this.maybeUpdateCheckout, '1000');
    },

    shipToDifferentAddress: function (e) {
        const target = e.target;

        if (!target.matches('#ship-to-different-address input')) {
            return;
        }

        const shippingAddress = document.querySelector('#shipping-fields-accordion-item');

        if (shippingAddress) {
            shippingAddress.classList.add('d-none');

            if (target.checked) {
                shippingAddress.classList.remove('d-none');

                // only toggle the accordion if user changed checkbox (initial view event is custom and differs from change)
                if (e.type === 'change') {
                    const accordion = shippingAddress.querySelector('.collapse');
                    const collapse = window.bootstrap.Collapse.getOrCreateInstance(accordion);
                    if (collapse) {
                        collapse.show();
                    }
                }
            }
        }
    },

    toggleCreateAccount: function (e) {
        const target = e.target;
        const createAccount = this.parentEl.querySelector('div.create-account');
        if (!createAccount) {
            return;
        }

        createAccount.classList.add('d-none');
        if (target.checked) {
            // ensure password is not pre-populated
            const password = this.parentEl.querySelector('#account_password');
            if (password) {
                password.value = '';
                password.dispatchEvent(this.events.change);
            }
            createAccount.classList.remove('d-none');
        }
    },

    initCheckout: function () {
        document.body.dispatchEvent(this.events.updateCheckout);
    },

    maybeUpdateCheckout: function () {
        let updateTotals = true;

        if (this.dirtyInput) {
            const requiredInputs = this.parentEl.querySelectorAll('.address-field.validate-required');

            if (requiredInputs) {
                requiredInputs.forEach((inputContainer) => {
                    const inputs = inputContainer.querySelectorAll('input.form-control');
                    inputs &&
                        inputs.forEach((input) => {
                            if (input.value === '') {
                                updateTotals = false;
                            }
                        });
                });
            }

            if (updateTotals) {
                this.triggerUpdateCheckout();
            }
        }
    },

    resetUpdateCheckoutTimer: function () {
        clearTimeout(this.updateTimer);
    },

    isValidJson: function (rawJson) {
        try {
            const json = JSON.parse(rawJson);

            return json && 'object' === typeof json;
        } catch (e) {
            return false;
        }
    },

    updateCheckout: function (event) {
        // small timeout to prevent multiple rquests when several fields update at the same time
        this.resetUpdateCheckoutTimer();
        this.updateTimer = setTimeout(this.updateCheckoutAction, '5', event.detail);
    },

    updateCheckoutAction: function (args) {
        if (this.fetch) {
            // todo: the abort does not work for some reason, check why > finally does correctly reset this.xhr, but when checking here it contains a promise again and aborts the current fetch
            // console.dir(this.fetch);
            // this.abortController.abort();
        }

        if (!document.querySelector('form.checkout')) {
            return;
        }

        args = typeof args !== 'undefined' ? args : { updateShippingMethod: true };

        const shipToDifferentAddress = this.parentEl.querySelector('#ship-to-different-address input')?.checked;
        const country = this.parentEl.querySelector('#billing_country')?.value;
        const state = this.parentEl.querySelector('#billing_state')?.value;
        const postcode = this.parentEl.querySelector('#billing_postcode')?.value;
        const city = this.parentEl.querySelector('#billing_city')?.value;
        const address = this.parentEl.querySelector('#billing_address_1')?.value;
        const address_2 = this.parentEl.querySelector('#billing_address_2')?.value;
        const s_country = shipToDifferentAddress ? this.parentEl.querySelector('#shipping_country')?.value : country;
        const s_state = shipToDifferentAddress ? this.parentEl.querySelector('#shipping_state')?.value : state;
        const s_postcode = shipToDifferentAddress ? this.parentEl.querySelector('#shipping_postcode')?.value : postcode;
        const s_city = shipToDifferentAddress ? this.parentEl.querySelector('#shipping_city')?.value : city;
        const s_address = shipToDifferentAddress ? this.parentEl.querySelector('#shipping_address_1')?.value : address;
        const s_address_2 = shipToDifferentAddress
            ? this.parentEl.querySelector('#shipping_address_2')?.value
            : address_2;
        let has_full_address = true;

        const requiredInputs = this.checkoutForm.querySelectorAll('.address-field.validate-required');
        if (requiredInputs) {
            requiredInputs.forEach((inputContainer) => {
                if (!inputContainer.classList.contains('d-none') && !inputContainer.closest('d-none')) {
                    const input = inputContainer.querySelector('input');
                    if (input && input.value === '') {
                        has_full_address = false;
                    }
                }
            });
        }

        const postData = new FormData(document.querySelector('form.checkout'));

        const data = {
            action: 'update_order_review',
            security: this.checkoutParams.updateOrderReviewNonce,
            payment_method: this.getPaymentMethod(),
            country,
            state,
            postcode,
            city,
            address,
            address_2,
            s_country,
            s_state,
            s_postcode,
            s_city,
            s_address,
            s_address_2,
            has_full_address,
            post_data: new URLSearchParams(postData).toString(),
        };

        if (args?.updateShippingMethod !== false) {
            const shippingMethods = {};
            const shippingInputs = document.querySelectorAll(
                'select.shipping_method, input[name^="shipping_method"][type="radio"]:checked, input[name^="shipping_method"][type="hidden"]'
            );
            shippingInputs &&
                shippingInputs.forEach((input) => {
                    shippingMethods[input.dataset.index] = input.value;
                });

            data.shipping_method = JSON.stringify(shippingMethods);
        }

        const fetchOptions = {
            url: this.checkoutParams.ajaxUrl,
            method: 'POST',
            signal: this.abortController.signal,
            body: new URLSearchParams(data),
        };
        this.fetch = fetchHandler(fetchOptions);
        this.fetch
            .then((result) => {
                // reload the page if requested
                if (result.reload === true) {
                    window.location.reload();
                    return;
                }

                // remove previous notices
                const prevNotices = document.querySelector('.woocommerce-NoticeGroup-updateOrderReview');
                prevNotices && prevNotices.remove();

                const termsCheckbox = document.querySelector('#terms');
                const termsCheckboxChecked = termsCheckbox && termsCheckbox.checked;

                // save payment details to a temporary object
                const paymentDetails = {};
                const paymentInputs = document.querySelectorAll(
                    '.payment-box input, .payment-box select, .payment-box textarea'
                );
                paymentInputs &&
                    paymentInputs.forEach((input) => {
                        const id = input.id;

                        if (id) {
                            if (input.type === 'checkbox' || input.type === 'radio') {
                                paymentDetails[id] = input.checked;
                            } else {
                                paymentDetails[id] = input.value;
                            }
                        }
                    });

                // always update the fragments
                if (result && result.fragments) {
                    for (const key in result.fragments) {
                        if (result.fragments.hasOwnProperty(key)) {
                            if (!this.fragments || this.fragments[key] !== result.fragments[key]) {
                                const element = document.querySelector(key);
                                if (element) {
                                    element.outerHTML = result.fragments[key];
                                }
                            }
                        }
                    }
                    this.fragments = result.fragments;
                }

                // recheck terms and conditions box, if needed
                if (termsCheckboxChecked) {
                    termsCheckbox.checked = true;
                }

                // Fill in the payment details if possible without overwriting data if set
                if (Object.entries(paymentDetails).length > 0) {
                    paymentInputs.forEach((input) => {
                        const id = input.id;

                        if (id) {
                            if (input.type === 'checkbox' || input.type === 'radio') {
                                input.checked = paymentDetails[id];
                                input.dispatchEvent(this.events.change);
                            } else if (input.type === 'select') {
                                input.value = paymentDetails[id];
                                input.dispatchEvent(this.events.change);
                            } else if (input.value !== null && input.value.length === 0) {
                                input.value = paymentDetails[id];
                                input.dispatchEvent(this.events.change);
                            }
                        }
                    });
                }

                // check for error
                if (result && result.result === 'failure') {
                    // remove notices from all sources
                    const form = document.querySelector('form.checkout');
                    const notices = form.querySelectorAll('.woocommerce-error, .woocommerce-message');
                    notices &&
                        notices.forEach((notice) => {
                            notice.remove();
                        });

                    // add ne errors returned by this event
                    if (result.messages) {
                        const noticeGroup = document.createElement('div');
                        noticeGroup.classList.add(
                            'woocommerce-NoticeGroup',
                            'woocommerce-NoticeGroup-updateOrderReview'
                        );
                        noticeGroup.innerHTML = result.messages;
                        form.prepend(noticeGroup);
                    } else {
                        const noticeData = document.createElement('div');
                        noticeData.innerHTML = data;
                        form.prepend(noticeData);
                    }

                    // lose focus for all fields
                    const fields = form.querySelectorAll('.form-control, select, [type="checkbox"]');
                    if (fields) {
                        fields.forEach((field) => {
                            field.dispatchEvent(this.events.validate);
                            field.dispatchEvent(this.events.blur);
                        });
                    }

                    this.scrollToNotices();
                }

                // re-init methods
                this.initPaymentMethods();

                // fire updatedCheckout event
                document.body.dispatchEvent(
                    new CustomEvent('updatedCheckout', {
                        detail: {
                            result,
                        },
                    })
                );
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                if (window.lazyLoadInstance) {
                    window.lazyLoadInstance.update();
                }

                this.xhr = false;
            });
    },

    handleUnloadEvent: function () {
        return true;
    },

    attachUnloadEventsOnSubmit: function () {
        window.addEventListener('beforeunload', this.handleUnloadEvent);
    },

    detachUnloadEventsOnSubmit: function () {
        window.removeEventListener('beforeunload', this.handleUnloadEvent);
    },

    blockOnSubmit: function (form) {
        // todo: implement method of preventing form edits upon submit
    },

    submitOrder: function () {
        this.blockOnSubmit(this.checkoutForm);
    },

    submit: function (e) {
        e.preventDefault();
        this.resetUpdateCheckoutTimer();
        const form = e.target;

        if (form.classList.contains('processing')) {
            return false;
        }

        // Trigger a handler to let gateways manipulate the checkout if needed
        if (
            this.triggerHandler('checkout_place_order', form) !== false &&
            this.triggerHandler(`checkout_place_order_${this.getPaymentMethod()}`, form) !== false
        ) {
            form.classList.add('processing');
            this.blockOnSubmit(form);

            // attach event to block reloading the page when the form has been submitted
            this.attachUnloadEventsOnSubmit();

            const postData = new FormData(form);
            // postData.action = 'checkout';
            const fetchBody = new URLSearchParams(postData);
            fetchBody.append('action', 'checkout');

            const submitOptions = {
                url: this.checkoutParams.ajaxUrl,
                method: 'POST',
                body: fetchBody,
            };

            const submission = fetchHandler(submitOptions);
            submission
                .then((result) => {
                    this.detachUnloadEventsOnSubmit();

                    if (
                        result.result === 'success' &&
                        this.triggerHandler('checkout_place_order_success', form, result) !== false
                    ) {
                        if (result.redirect.indexOf('https://') === -1 || result.redirect.indexOf('http://') === -1) {
                            window.location = result.redirect;
                        } else {
                            window.location = decodeURI(result.redirect);
                        }
                    } else {
                        if (result.result === 'failure') {
                            console.log('failure', result);
                        } else {
                            console.log('invalid', result);
                        }

                        if (result.reload === true) {
                            window.location.reload();
                            return;
                        }

                        // trigger update in case we need a fresh nonce
                        if (result.refresh) {
                            document.body.dispatchEvent(this.events.updateCheckout);
                        }

                        // add new errors
                        if (result.messages) {
                            this.submitError(result.messages);
                        } else {
                            const container = document.createElement('div');
                            container.classList.add('woocommerce-error');
                            container.textContent = this.checkoutParams.i18nCheckoutError;
                            this.submitError(container);
                        }
                    }
                })
                .catch((error) => {
                    this.detachUnloadEventsOnSubmit();
                    const container = document.createElement('div');
                    container.classList.add('woocommerce-error');
                    container.textContent = error;
                    this.submitError(container);
                })
                .finally(() => {
                    if (window.lazyLoadInstance) {
                        window.lazyLoadInstance.update();
                    }
                });
        }

        return false;
    },

    submitError: function (error) {
        const noticeContainer = document.querySelector('.ct_woocommercenotices.default');
        let notices = null;

        if (noticeContainer) {
            notices = noticeContainer.querySelectorAll('.woocommerce-NoticeGroup-checkout');
        } else {
            notices = document.querySelectorAll('.woocommerce-NoticeGroup-checkout');
        }

        notices && notices.forEach((notice) => notice.remove());

        let errorElement = null;
        if (error.nodeName !== undefined) {
            errorElement = error;
        } else {
            errorElement = document.createElement('div');
            errorElement.classList.add('woocommerce-error', 'alert', 'alert-danger');
            errorElement.innerHTML = error;
            errorElement.querySelector('ul')?.classList.add('mb-0');
        }

        const noticeGroup = document.createElement('div');
        noticeGroup.classList.add('woocommerce-NoticeGroup', 'woocommerce-NoticeGroup-checkout');
        noticeGroup.appendChild(errorElement);
        if (noticeContainer) {
            noticeContainer.appendChild(noticeGroup);
        } else {
            this.checkoutForm.prepend(noticeGroup);
        }
        this.checkoutForm.classList.remove('processing');
        const inputs = this.checkoutForm.querySelectorAll('.form-control, select, [type="checkbox"]');
        inputs &&
            inputs.forEach((input) => {
                input.dispatchEvent(this.events.validate);
                input.dispatchEvent(this.events.blur);
                this.scrollToNotices();
                document.body.dispatchEvent(
                    new CustomEvent('checkoutError', {
                        detail: {
                            error: error.textContent,
                        },
                    })
                );
            });
    },

    scrollToNotices: function () {
        let scrollElement = document.querySelector(
            '.woocommerce-NoticeGroup-updateOrderReview, .woocommerce-NoticeGroup-checkout'
        );

        if (!scrollElement) {
            scrollElement = document.querySelector('form.checkout');
        }

        scrollElement.scrollIntoView({ behavior: 'smooth' });
    },

    triggerHandler: function (type, element, data = {}) {
        const event = new CustomEvent(type);
        event.preventDefault();
        event.stopPropagation();
        element.dispatchEvent(event, { detail: { data } });
        return event.result;
    },
};
